import React, { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import styled from 'styled-components';
import { calculateChange } from './change_calculations';
import { calculateSummary } from './summary_calculations';
import { calculateSummary as calculateManagerReport } from './manager_report';
import { calculateSummary as calculateManagerReportV2 } from './manager_v2';

const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Raleway', sans-serif;
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #2A3039;
  margin: 0 0 8px 0;
  font-family: 'Raleway', sans-serif;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666D79;
  margin: 0;
  font-weight: 400;
`;

const ControlsSection = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const ControlGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #2A3039;
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid #DFDFDF;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  background: white;
  color: #2A3039;
  min-width: 200px;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #27CDA7;
  }

  &:focus {
    outline: none;
    border-color: #2D70E2;
    box-shadow: 0 0 0 3px rgba(45, 112, 226, 0.1);
  }

  &:disabled {
    background: #F2F2F2;
    cursor: not-allowed;
    color: #B3B3B3;
  }
`;

const TriggerButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #27CDA7 0%, #2D70E2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Raleway', sans-serif;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-height: 44px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(45, 112, 226, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #B3B3B3;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ResultsSection = styled.div`
  margin-top: 24px;
`;

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const ResultsTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #2A3039;
  margin: 0;
`;

const CopyButton = styled.button`
  padding: 8px 16px;
  background: white;
  color: #2D70E2;
  border: 2px solid #2D70E2;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  font-family: 'Raleway', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #2D70E2;
    color: white;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const JsonContainer = styled.div`
  background: #F8FAFF;
  border: 1px solid #DFDFDF;
  border-radius: 8px;
  padding: 20px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  line-height: 1.5;
  color: #2A3039;
  overflow-x: auto;
  max-height: 500px;
  overflow-y: auto;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  
  &::after {
    content: '';
    width: 32px;
    height: 32px;
    border: 3px solid #DFDFDF;
    border-top: 3px solid #27CDA7;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #FEF3F4;
  border: 1px solid #F15062;
  border-radius: 8px;
  padding: 16px;
  color: #F15062;
  font-size: 14px;
  margin-top: 16px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666D79;
  font-size: 14px;
`;

const StatusText = styled.p`
  font-size: 12px;
  color: #666D79;
  margin-top: 8px;
  font-style: italic;
`;

const TokenUsageDisplay = styled.div`
  background: #F0F7FF;
  border: 2px solid #2D70E2;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  color: #2A3039;
`;

const TokenUsageTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #2D70E2;
  font-family: 'Raleway', sans-serif;
`;

const TokenRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  
  &:last-child {
    margin-bottom: 0;
    padding-top: 8px;
    border-top: 1px solid #DFDFDF;
    font-weight: 600;
  }
`;

const TokenLabel = styled.span`
  color: #666D79;
`;

const TokenValue = styled.span`
  color: #2A3039;
  font-weight: 600;
`;

const CostValue = styled.span`
  color: #27CDA7;
  font-weight: 600;
  font-size: 13px;
`;

const PricingNote = styled.div`
  font-size: 10px;
  color: #666D79;
  margin-top: 8px;
  font-style: italic;
  border-top: 1px solid #DFDFDF;
  padding-top: 8px;
`;

const ReportV2 = () => {
    const [selectedCalculation, setSelectedCalculation] = useState('changeAnalysis');
    const [selectedAnchorParent, setSelectedAnchorParent] = useState('');
    const [selectedModel, setSelectedModel] = useState('gpt-4.1');
    const [calculationResult, setCalculationResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const [tokenUsage, setTokenUsage] = useState({
        prompt_tokens: 0,
        completion_tokens: 0,
        total_tokens: 0,
        call_count: 0,
        input_cost: 0,
        output_cost: 0,
        total_cost: 0
    });

    const core_data = useSelector(
        (state) => state.audit?.core_data,
        shallowEqual
    );

    console.log("core_data", core_data);
    const raw_data = useSelector((state) => state.audit?.raw_data, shallowEqual);
    const debriefSchedules = useSelector((state) => state.debrief_schedule, shallowEqual);

    // Get anchor parents from core_data
    const anchorParents = core_data?.categories?.[0]?.categories || [];

    // Model options with pricing
    const modelOptions = [
        {
            value: 'gpt-4.1',
            label: 'GPT-4.1',
            inputCostPerMillion: 2.00,
            outputCostPerMillion: 8.00
        },
        {
            value: 'gpt-4.1-mini',
            label: 'GPT-4.1 Mini',
            inputCostPerMillion: 0.40,
            outputCostPerMillion: 1.60
        },
        {
            value: 'gpt-4.1-nano',
            label: 'GPT-4.1 Nano',
            inputCostPerMillion: 0.10,
            outputCostPerMillion: 0.40
        },
        {
            value: 'grok-4-latest',
            label: 'Grok 4',
            inputCostPerMillion: 3.00,
            outputCostPerMillion: 15.00
        }
    ];

    // Get current model pricing
    const currentModel = modelOptions.find(model => model.value === selectedModel);

    // Set default anchor parent if not selected and data is available
    useEffect(() => {
        if (anchorParents.length > 0 && !selectedAnchorParent) {
            setSelectedAnchorParent(anchorParents[0].id);
        }
    }, [anchorParents, selectedAnchorParent]);

    const calculationOptions = [
        { value: 'changeAnalysis', label: 'Change Analysis', func: calculateChange },
        { value: 'summaryAnalysis', label: 'Summary Analysis', func: calculateSummary },
        { value: 'managerReport', label: 'Manager Report', func: calculateManagerReport },
        { value: 'managerReportV2', label: 'Manager Report V2', func: calculateManagerReportV2 }
    ];

    const canCalculate = raw_data && core_data && selectedAnchorParent;

    const handleCalculate = async () => {
        if (!canCalculate) return;

        setLoading(true);
        setError(null);
        setCalculationResult(null);
        // Reset token usage
        setTokenUsage({
            prompt_tokens: 0,
            completion_tokens: 0,
            total_tokens: 0,
            call_count: 0,
            input_cost: 0,
            output_cost: 0,
            total_cost: 0
        });

        try {
            // Find the selected calculation function
            const selectedOption = calculationOptions.find(option => option.value === selectedCalculation);
            if (!selectedOption) {
                throw new Error('Invalid calculation type selected');
            }

            // Create token update callback with cost calculation
            const handleTokenUpdate = (newTokenUsage) => {
                // Calculate costs using selected model pricing
                const inputCost = (newTokenUsage.prompt_tokens / 1000000) * currentModel.inputCostPerMillion;
                const outputCost = (newTokenUsage.completion_tokens / 1000000) * currentModel.outputCostPerMillion;
                const totalCost = inputCost + outputCost;

                setTokenUsage({
                    ...newTokenUsage,
                    input_cost: inputCost,
                    output_cost: outputCost,
                    total_cost: totalCost
                });
            };

            // Pass the selected anchor parent ID, selected model, and token callback to the calculation function
            const result = await selectedOption.func(raw_data, core_data, debriefSchedules, selectedAnchorParent, handleTokenUpdate, selectedModel);
            setCalculationResult(result);
        } catch (err) {
            console.error('Calculation error:', err);
            setError(err.message || 'An error occurred during calculation');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyToClipboard = async () => {
        if (!calculationResult) return;

        try {
            await navigator.clipboard.writeText(JSON.stringify(calculationResult, null, 2));
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
        }
    };

    return (
        <Container>
            <Header>
                <Title>Report Analysis v2</Title>
                <Subtitle>Select a calculation type and anchor parent to generate detailed analysis results</Subtitle>
            </Header>

            <ControlsSection>
                <ControlGroup>
                    <Label htmlFor="calculation-select">Calculation Type</Label>
                    <Select 
                        id="calculation-select"
                        value={selectedCalculation}
                        onChange={(e) => setSelectedCalculation(e.target.value)}
                        disabled={loading}
                    >
                        {calculationOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                </ControlGroup>

                <ControlGroup>
                    <Label htmlFor="anchor-parent-select">Anchor Parent</Label>
                    <Select 
                        id="anchor-parent-select"
                        value={selectedAnchorParent}
                        onChange={(e) => setSelectedAnchorParent(e.target.value)}
                        disabled={loading || anchorParents.length === 0}
                    >
                        {anchorParents.length === 0 ? (
                            <option value="">No anchor parents available</option>
                        ) : (
                            anchorParents.map(parent => (
                                <option key={parent.id} value={parent.id}>
                                    {parent.name || `Anchor Parent ${parent.id}`}
                                </option>
                            ))
                        )}
                    </Select>
                </ControlGroup>

                <ControlGroup>
                    <Label htmlFor="model-select">Model</Label>
                    <Select 
                        id="model-select"
                        value={selectedModel}
                        onChange={(e) => setSelectedModel(e.target.value)}
                        disabled={loading}
                    >
                        {modelOptions.map(model => (
                            <option key={model.value} value={model.value}>
                                {model.label}
                            </option>
                        ))}
                    </Select>
                </ControlGroup>

                <TriggerButton 
                    onClick={handleCalculate}
                    disabled={!canCalculate || loading}
                >
                    {loading ? 'Calculating...' : 'Run Calculation'}
                </TriggerButton>
            </ControlsSection>

            {(loading || tokenUsage.call_count > 0) && (
                <TokenUsageDisplay>
                    <TokenUsageTitle>
                        🤖 LLM Token Usage {loading && '(Real-time)'}
                    </TokenUsageTitle>
                    <TokenRow>
                        <TokenLabel>API Calls:</TokenLabel>
                        <TokenValue>{tokenUsage.call_count}</TokenValue>
                    </TokenRow>
                    <TokenRow>
                        <TokenLabel>Input Tokens:</TokenLabel>
                        <TokenValue>{tokenUsage.prompt_tokens.toLocaleString()}</TokenValue>
                    </TokenRow>
                    <TokenRow>
                        <TokenLabel>Output Tokens:</TokenLabel>
                        <TokenValue>{tokenUsage.completion_tokens.toLocaleString()}</TokenValue>
                    </TokenRow>
                    <TokenRow>
                        <TokenLabel>Total Tokens:</TokenLabel>
                        <TokenValue>{tokenUsage.total_tokens.toLocaleString()}</TokenValue>
                    </TokenRow>
                    <TokenRow>
                        <TokenLabel>Input Cost:</TokenLabel>
                        <CostValue>${tokenUsage.input_cost.toFixed(4)}</CostValue>
                    </TokenRow>
                    <TokenRow>
                        <TokenLabel>Output Cost:</TokenLabel>
                        <CostValue>${tokenUsage.output_cost.toFixed(4)}</CostValue>
                    </TokenRow>
                    <TokenRow>
                        <TokenLabel>Total Cost:</TokenLabel>
                        <CostValue>${tokenUsage.total_cost.toFixed(4)}</CostValue>
                    </TokenRow>
                    <PricingNote>
                        {currentModel.label} Pricing: ${currentModel.inputCostPerMillion} per 1M input tokens • ${currentModel.outputCostPerMillion} per 1M output tokens
                    </PricingNote>
                </TokenUsageDisplay>
            )}

            {!canCalculate && (
                <StatusText>
                    Waiting for required data... 
                    {!raw_data && 'Missing raw data. '}
                    {!core_data && 'Missing core data. '}
                    {!selectedAnchorParent && 'Please select an anchor parent.'}
                </StatusText>
            )}

            {error && (
                <ErrorMessage>
                    <strong>Error:</strong> {error}
                </ErrorMessage>
            )}

            {loading && <LoadingSpinner />}

            {calculationResult && (
                <ResultsSection>
                    <ResultsHeader>
                        <ResultsTitle>Calculation Results</ResultsTitle>
                        <CopyButton onClick={handleCopyToClipboard}>
                            {copySuccess ? '✓ Copied!' : 'Copy JSON'}
                        </CopyButton>
                    </ResultsHeader>
                    <JsonContainer>
                        <pre>{JSON.stringify(calculationResult, null, 2)}</pre>
                    </JsonContainer>
                </ResultsSection>
            )}

            {!calculationResult && !loading && !error && canCalculate && (
                <EmptyState>
                    Select a calculation type and anchor parent, then click "Run Calculation" to see results
                </EmptyState>
            )}
        </Container>
    );
};

export default ReportV2;